// plugins/chunkError.client.ts — turn a failed chunk load into a screen the
// guest can act on (SPEC §9).
//
// `experimental.emitRouteChunkError` is set to 'manual' in nuxt.config, so Nuxt
// hands us `app:chunkError` and does nothing else. The default ('automatic')
// waits for the guest to navigate before reloading, which they never do — they
// are looking at a screen that stopped responding, so they close the app.
//
// `app:chunkError` alone is not enough: it only fires for failures the ROUTER
// sees. The common case in this app is a guest tapping "สั่งอาหาร", where
// `navigateTo()` rejects inside the click handler and the error reaches Vue
// instead — verified with a blocked chunk in tests/e2e/resilience.spec.ts. So
// we listen on every path an import failure can take.
//
// We only raise the flag; `components/v3/App/ConnectionError.vue` owns the UI
// and the reload.
import { defineNuxtPlugin } from '#imports'
import { isChunkError, useChunkError } from '~/composables/v3/useChunkError'

export default defineNuxtPlugin((nuxtApp) => {
  const chunkError = useChunkError()

  const raise = (source: string, error: unknown): void => {
    console.warn(`[v3/chunkError] a chunk failed to load (${source})`, error)
    chunkError.value = true
  }

  // 1. the router noticed — Nuxt already classified it as a chunk error
  nuxtApp.hook('app:chunkError', ({ error }) => raise('app:chunkError', error))

  // 2. it escaped through a component: a click handler awaiting navigateTo()
  nuxtApp.hook('vue:error', (error) => {
    if (isChunkError(error)) raise('vue:error', error)
  })

  // 3. nothing caught it at all
  if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
      if (isChunkError(event.reason)) raise('unhandledrejection', event.reason)
    })
    window.addEventListener('error', (event) => {
      if (isChunkError(event.error ?? event.message)) raise('window.error', event.error ?? event.message)
    })
  }
})
